import { useCallback, useEffect, useRef, useState } from "react";
import { prefersReduce } from "./hooks";
import { CUES } from "./data";

/**
 * Plasma "signal spine": fixed left rail canvas.
 * - straight glowing hairline at rest with a faint slow flex
 * - widens where the scroll playhead reaches a section (cue)
 * - mouse-proximity shockwaves near the rail
 * - playhead node + timecode track scroll; amplitude clamped inside the rail
 * Reads --accent / --bg live so it follows theme + accent. Hidden < 1001px via CSS.
 */
export function SignalSpine({ motion }: { motion: boolean }) {
  const [progress, setProgress] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const st = useRef({ progress: 0, mouseY: -999, near: false, ripples: [] as { y: number; age: number }[], lastRipple: 0, t: 0, marks: [] as number[] }).current;

  const measure = useCallback(() => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    st.marks = CUES.map((c) => {
      const el = document.getElementById(c.id);
      const top = el ? el.getBoundingClientRect().top + window.scrollY : 0;
      return Math.max(0, Math.min(1, max > 0 ? top / max : 0));
    });
  }, [st]);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        setProgress(p); st.progress = p;
      });
    };
    const onResize = () => { measure(); onScroll(); };
    measure(); onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    const to = window.setTimeout(measure, 600);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); clearTimeout(to); };
  }, [measure, st]);

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d")!;
    const anim = motion && !prefersReduce();
    const CORE = [255, 227, 214];
    // Read accent/bg/theme from the landing scope so nothing depends on <html>
    // (keeps the app design-system tokens in tokens.css untouched).
    const scopeEl = (cv.closest("[data-lpv2]") as HTMLElement | null) ?? document.documentElement;
    const rootStyle = getComputedStyle(scopeEl);
    const hexRGB = (h: string) => { h = (h || "#ff6a3d").trim().replace("#", ""); if (h.length === 3) h = h.split("").map((c) => c + c).join(""); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; };
    const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
    const accentStr = () => rootStyle.getPropertyValue("--accent").trim() || "#ff6a3d";
    let ACC = hexRGB(accentStr());
    let BG = hexRGB(rootStyle.getPropertyValue("--bg").trim() || "#0a0a0c");
    let LIGHT = scopeEl.getAttribute("data-theme") === "light";
    let W = 0, H = 0, raf = 0, last = performance.now(), tick = 0;

    const fit = () => {
      const r = cv.parentElement!.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height;
      cv.width = Math.max(1, Math.round(W * dpr)); cv.height = Math.max(1, Math.round(H * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    const onResize = () => fit();
    window.addEventListener("resize", onResize);
    const onMove = (e: MouseEvent) => {
      st.near = e.clientX < W + 96; st.mouseY = e.clientY;
      if (st.near && anim) { const now = performance.now(); if (now - st.lastRipple > 85) { st.ripples.push({ y: e.clientY, age: 0 }); st.lastRipple = now; if (st.ripples.length > 16) st.ripples.shift(); } }
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    const ampAt = (y: number) => {
      let a = 0;
      for (const m of st.marks) { const prox = Math.exp(-Math.pow((st.progress - m) / 0.05, 2)); if (prox > 0.03) a += 14 * prox * Math.exp(-Math.pow((y - m * H) / 48, 2)); }
      if (st.near) a += 18 * Math.exp(-Math.pow((y - st.mouseY) / 80, 2));
      for (const r of st.ripples) { const front = r.age * 0.55; a += 20 * Math.exp(-r.age / 600) * Math.exp(-Math.pow((Math.abs(y - r.y) - front) / 30, 2)); }
      return Math.min(a, W / 2 - 8);
    };
    const xAt = (y: number) => { const idle = anim ? 1.8 * Math.sin(y * 0.0032 + st.t * 0.0007) : 0; return W / 2 + idle + ampAt(y) * Math.sin(y * 0.05); };
    const build = (yBot: number) => { ctx.beginPath(); let f = true; for (let y = 0; y <= yBot; y += 4) { const x = xAt(y); if (f) { ctx.moveTo(x, y); f = false; } else ctx.lineTo(x, y); } };

    const frame = (now: number) => {
      const dt = Math.min(48, now - last); last = now;
      if (anim) { st.t += dt; for (const r of st.ripples) r.age += dt; st.ripples = st.ripples.filter((r) => r.age < 1400); }
      if ((tick++ % 24) === 0) { ACC = hexRGB(accentStr()); BG = hexRGB(rootStyle.getPropertyValue("--bg").trim() || "#0a0a0c"); LIGHT = scopeEl.getAttribute("data-theme") === "light"; }
      const playY = st.progress * H, CO = LIGHT ? ACC : CORE, k = LIGHT ? 1.9 : 1;
      ctx.fillStyle = rgba(BG, 0.3); ctx.fillRect(0, 0, W, H);
      ctx.save(); ctx.globalCompositeOperation = LIGHT ? "source-over" : "lighter"; ctx.lineCap = "round";
      build(H); ctx.strokeStyle = rgba(ACC, 0.42 * k); ctx.lineWidth = 1; ctx.stroke();
      build(playY); ctx.strokeStyle = rgba(CO, 0.85); ctx.lineWidth = 1.2; ctx.shadowColor = accentStr(); ctx.shadowBlur = 6; ctx.stroke(); ctx.shadowBlur = 0;
      const px = xAt(playY), pr = 0.6 + 0.4 * Math.sin(st.t * 0.006);
      const g = ctx.createRadialGradient(px, playY, 0, px, playY, 12 + 4 * pr);
      g.addColorStop(0, rgba(CO, 0.8)); g.addColorStop(0.5, rgba(ACC, 0.35)); g.addColorStop(1, rgba(ACC, 0));
      ctx.fillStyle = g; ctx.fillRect(px - 18, playY - 18, 36, 36);
      ctx.beginPath(); ctx.arc(px, playY, 2.2, 0, 7); ctx.fillStyle = rgba(CO, 1); ctx.fill();
      ctx.restore();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); window.removeEventListener("mousemove", onMove); };
  }, [motion, st]);

  const total = 504, cur = Math.round(progress * total);
  const tc = `00:${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`;
  const activeIdx = st.marks.reduce((acc, m, i) => (progress + 0.001 >= m ? i : acc), 0);

  return (
    <div className="spine" aria-hidden="true">
      <div className="spine__rail" />
      <canvas className="spine__canvas" ref={canvasRef} />
      <span className="spine__label spine__label--in">IN</span>
      <span className="spine__label spine__label--out">OUT</span>
      <div className="spine__cues">
        {CUES.map((c, i) => (
          <div key={c.id} className="spine__cue" data-on={i <= activeIdx ? "1" : "0"} data-active={i === activeIdx ? "1" : "0"}
            style={{ top: `${(st.marks[i] || 0) * 100}%`, position: "absolute" }}>
            <span className="spine__tick" />
          </div>
        ))}
      </div>
      <div className="spine__tc" style={{ top: `${progress * 100}%` }}>{tc}</div>
    </div>
  );
}
